import { useEffect, type FunctionComponent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Form, Input, Select } from "antd";
import { type FormInstance } from "antd/lib/form";
import { useTranslation } from "react-i18next";

import { supabase } from "~/store/initializeStore";

import { ScheduleTimingFields } from "../ScheduleTimingFields/ScheduleTimingFields";
import { TimeField } from "../TimeField/TimeField";
import { type AssignHabitsTaskFormModel } from "./types";

interface OwnProps {
  formController?: FormInstance<AssignHabitsTaskFormModel>;
  onSubmit: (formData: AssignHabitsTaskFormModel) => void;
  // Partial: package-add passes only a scheduleTiming preset, not a full model.
  model?: Partial<AssignHabitsTaskFormModel> | null;
}

type Props = OwnProps;

const AssignHabitsTaskForm: FunctionComponent<Props> = ({ formController, onSubmit, model }) => {
  const { t } = useTranslation(["automation", "common"]);

  const habitTemplatesQuery = useQuery({
    queryKey: ["automation-habit-templates"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("habit_template")
        .select("id, name")
        .eq("archived", false)
        .order("name")
        .returns<{ id: string; name: string }[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  useEffect(() => {
    // reset-then-apply so a preset (or edited task) never leaves stale values
    // from a previous open in the shared form controller.
    formController?.resetFields();
    if (model) {
      formController?.setFieldsValue(model);
    }
  }, [model, formController]);

  return (
    <div>
      <Form<AssignHabitsTaskFormModel>
        name="assign-habits-task-form"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 14 }}
        layout="horizontal"
        form={formController}
        initialValues={{
          name: "",
          habitTemplateIds: [],
          sentTimeConfig: { mode: "absolute", avoidSendingOnTheNight: true },
          ...model,
        }}
        onFinish={onSubmit}
      >
        <Form.Item
          name="name"
          label="Nazwa"
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Input placeholder="np. Nawyki startowe" />
        </Form.Item>

        <Form.Item
          name="habitTemplateIds"
          label="Nawyki do przypisania"
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Select
            mode="multiple"
            loading={habitTemplatesQuery.isLoading}
            placeholder="Wybierz nawyki"
            optionFilterProp="label"
            options={(habitTemplatesQuery.data ?? []).map((template) => ({
              value: template.id,
              label: template.name,
            }))}
          />
        </Form.Item>

        <TimeField showOnlyAbsolute={false} label={t<string>("automation:automatedMessageForm.operationTime")} />

        <ScheduleTimingFields formController={formController} />
      </Form>
    </div>
  );
};

export default AssignHabitsTaskForm;
